import { useQuery } from "@tanstack/react-query";
import { isUiMockMode } from "@/config/features";
import { filterMockCreditNotes } from "@/fixtures/creditNotes";
import { searchCreditNotes } from "@/features/creditNotes/api/search";
import type { CreditNoteRow } from "@/types/api";
import { isInvoiceAmountFilterValid } from "@/domain/invoices/invoiceFilterState";
import { keepPreviousPageData, paginatedSearchQueryOptions } from "@/lib/query/paginatedSearch";
import { isoDateToUnixEnd, isoDateToUnixStart } from "@/utils/format/isoDate";

function majorToMinorUnits(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}

export function useCreditNoteSearch(params: {
  customerIds: string[];
  statuses: string[];
  dateFrom: string;
  dateTo: string;
  amountMin: string;
  amountMax: string;
  pageSize: number;
  after?: string;
}) {
  const customer_ids =
    params.customerIds.length > 0 ? params.customerIds : undefined;
  const statuses =
    params.statuses.length > 0 ? params.statuses : undefined;
  const amountOk = isInvoiceAmountFilterValid(
    params.amountMin,
    params.amountMax,
  );

  const queryKey = {
    ...params,
    createdFrom: isoDateToUnixStart(params.dateFrom),
    createdTo: isoDateToUnixEnd(params.dateTo),
    amountMinMinor: amountOk ? majorToMinorUnits(params.amountMin) : undefined,
    amountMaxMinor: amountOk ? majorToMinorUnits(params.amountMax) : undefined,
  };

  return useQuery({
    queryKey: ["creditNotes", "search", queryKey],
    queryFn: async (): Promise<CreditNoteSearchResult> => {
      if (isUiMockMode()) {
        const items = filterMockCreditNotes(params);
        return { items, has_more: false, next_cursor: null as string | null };
      }
      const res = await searchCreditNotes({
        page: 0,
        page_size: params.pageSize,
        statuses,
        customer_ids,
        after_credit_note_id: params.after,
        created_from: queryKey.createdFrom,
        created_to: queryKey.createdTo,
        amount_min: queryKey.amountMinMinor,
        amount_max: queryKey.amountMaxMinor,
      });
      return {
        items: res.items.map(toFixtureCompatibleRow),
        has_more: res.has_more,
        next_cursor: res.next_cursor,
      };
    },
    ...paginatedSearchQueryOptions,
    placeholderData: keepPreviousPageData<CreditNoteSearchResult>(queryKey),
  });
}

type CreditNoteSearchResult = {
  items: CreditNoteRow[];
  has_more: boolean;
  next_cursor: string | null;
};

function toFixtureCompatibleRow(row: CreditNoteRow): CreditNoteRow {
  return {
    ...row,
    number: row.number ?? "",
    invoice_id: row.invoice_id ?? "",
    invoice_number: row.invoice_number ?? "",
    customer_name: row.customer_name ?? "",
  };
}
